import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { API, api, fmtErr, fmtDate, fmtDT, fmtQty, fileUrl } from "../api";
import StatusBadge from "../components/StatusBadge";
import { QcChecklist } from "../components/BoxesPanel";
import { Printer } from "lucide-react";

export default function BoxDetail() {
  const { id } = useParams();
  const [box, setBox] = useState(null);

  const load = useCallback(() => {
    api.get(`/boxes/${id}`).then((r) => setBox(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!box) return <div className="text-slate-500" data-testid="box-loading">Loading box...</div>;
  const qc = box.qc || {};

  return (
    <div data-testid="box-detail-page" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl font-extrabold font-mono text-[#002B5D] tracking-tight" data-testid="box-id">{box.box_id}</h1>
            <StatusBadge status={box.status} testid="box-status" />
            {box.status === "Completed" && <StatusBadge status={qc.status || "Passed"} testid="box-qc-status" />}
          </div>
          <p className="text-slate-500 mt-1">{box.product_name} <span className="font-mono text-xs">({box.product_code})</span></p>
        </div>
        <a href={`${API}/labels.pdf?box_ids=${id}`} download data-testid="box-print-label-button"
          className="h-11 px-5 rounded-md font-heading font-bold text-sm text-white inline-flex items-center gap-2" style={{ backgroundColor: "#002B5D" }}>
          <Printer className="w-4 h-4" /> Download QR Label PDF
        </a>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="sk-card p-5 space-y-3" data-testid="box-info">
          <h3 className="font-heading font-bold text-[#002B5D]">Box Information</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-slate-500">Lot</dt><dd><Link to={`/lots/${box.lot_id}`} className="font-mono text-[#EB1C27] hover:underline">{box.lot_number}</Link></dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Box Number</dt><dd className="font-mono font-semibold">{box.box_number}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Manufacturing Order</dt><dd><Link to={`/manufacturing/orders/${box.mo_id}`} className="font-mono text-[#EB1C27] hover:underline">{box.mo_number}</Link></dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Quantity</dt><dd className="font-mono font-semibold" data-testid="box-qty">{fmtQty(box.qty)} {box.uom}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Available</dt><dd className="font-mono font-semibold text-green-700" data-testid="box-remaining">{fmtQty(box.remaining_qty)} {box.uom}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Start Time</dt><dd className="font-mono text-xs" data-testid="box-started">{fmtDT(box.started_at)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">End Time</dt><dd className="font-mono text-xs" data-testid="box-completed">{fmtDT(box.completed_at)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Production Time</dt><dd className="font-mono font-semibold" data-testid="box-duration">
              {box.duration_minutes != null ? `${Math.floor(box.duration_minutes / 60)}h ${Math.round(box.duration_minutes % 60)}m` : "-"}
            </dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Operator</dt><dd>{box.operator || "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Production Date</dt><dd>{box.completed_at ? fmtDate(box.completed_at) : "-"}</dd></div>
          </dl>
        </div>

        <div className="sk-card p-5 space-y-3" data-testid="box-qc-info">
          <h3 className="font-heading font-bold text-[#002B5D]">QC Information</h3>
          {box.status !== "Completed" && <QcChecklist box={box} />}
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-slate-500">Box Weight (Net)</dt><dd className="font-mono" data-testid="box-qc-net">{qc.box_weight ?? "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Number of Joints</dt><dd className="font-mono" data-testid="box-qc-joints">{qc.joints ?? "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Zipper Width</dt><dd className="font-mono">{qc.width ?? "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Zipper Weight</dt><dd className="font-mono">{qc.zipper_weight ?? "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">QC Remarks</dt><dd className="text-right">{qc.remarks || "-"}</dd></div>
          </dl>
          {qc.photo ? (
            <img src={fileUrl(qc.photo)} alt="Lock / seal" className="w-full rounded border border-border" data-testid="box-photo" />
          ) : (
            <div className="border border-dashed border-border rounded-md px-4 py-6 text-center text-xs text-slate-400">No lock/seal photo</div>
          )}
        </div>

        <div className="sk-card p-5" data-testid="box-qr-card">
          <h3 className="font-heading font-bold text-[#002B5D] mb-3">QR Code</h3>
          <img src={`${API}/boxes/${id}/qr`} alt={`QR ${box.box_id}`} className="w-36 h-36 border border-border rounded" data-testid="box-qr-image" />
          <p className="text-[10px] text-slate-400 mt-1">Scan to open this box</p>
          <p className="font-mono text-xs font-semibold text-[#002B5D] mt-2">{box.box_id}</p>
        </div>
      </div>
    </div>
  );
}
